import { Link, Navigate, useNavigate } from "react-router"
import { ArrowLeft, Trash2 } from "lucide-react"

import {
  useArticleArchive,
  useDeleteArchivedArticle,
} from "@/api/hooks/articleGenerator"
import { useAuth } from "@/auth/AuthProvider"

function formatDate(value: string | null) {
  if (!value) return "—"
  try {
    return new Date(value).toLocaleString()
  } catch {
    return value
  }
}

export default function ArticleArchiveTab() {
  const { canUseArticleGenerator } = useAuth()
  const navigate = useNavigate()
  const archive = useArticleArchive(canUseArticleGenerator)
  const remove = useDeleteArchivedArticle()

  if (!canUseArticleGenerator) {
    return <Navigate to="/tools/url-encoder" replace />
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-4">
      <div className="flex items-center justify-between gap-3">
        <Link
          to="/tools/article-generator"
          className="inline-flex items-center gap-1.5 text-sm text-[#8b9aff] hover:underline"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          Back to generator
        </Link>
        <p className="text-sm text-[#aaa]">
          {archive.data ? `${archive.data.total} saved` : "Archive"}
        </p>
      </div>

      {archive.isLoading ? (
        <p className="text-sm text-[#aaa]">Loading archive…</p>
      ) : null}

      {archive.isError ? (
        <p className="rounded border border-red-500/40 bg-red-950/40 px-3 py-2 text-sm text-red-200">
          Failed to load archive
        </p>
      ) : null}

      {archive.data && archive.data.items.length === 0 ? (
        <p className="text-sm text-[#888]">No saved articles yet. Generate one to archive it.</p>
      ) : null}

      <div className="space-y-2">
        {archive.data?.items.map((item) => (
          <div
            key={item.id}
            className="flex items-start gap-3 rounded-lg border border-[#555] bg-[#252525] px-4 py-3 transition-colors hover:border-[#8b9aff]/50 hover:bg-[#323232]"
          >
            <button
              type="button"
              className="min-w-0 flex-1 text-left"
              onClick={() => navigate(`/tools/article-generator/archive/${item.id}`)}
            >
              <p className="truncate font-medium text-white">{item.title}</p>
              <p className="mt-1 text-xs text-[#888]">
                {formatDate(item.created_at)}
                {item.creator_username ? ` · ${item.creator_username}` : ""}
                {" · "}
                {item.language}
                {" · "}
                {item.countries.slice(0, 2).join(", ")}
                {item.countries.length > 2 ? "…" : ""}
                {" · "}
                {item.word_count} words · {item.provider}
                {item.status && item.status !== "completed"
                  ? ` · ${item.status}`
                  : ""}
              </p>
            </button>
            <button
              type="button"
              aria-label={`Delete ${item.title}`}
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm("Delete this archived article?")) {
                  void remove.mutateAsync(item.id)
                }
              }}
              className="rounded border border-[#555] bg-[#3a3a3a] p-2 text-[#aaa] hover:bg-[#444] hover:text-white disabled:opacity-40"
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}
